import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes, useLocation, useSearchParams, type Location } from 'react-router'

import { AppShell } from '@/components/layout/app-shell'
import { Spinner } from '@/components/ui/misc'
import GalleryPage from '@/pages/gallery-page'

const ImageViewer = lazy(() => import('@/pages/image-viewer'))
const UploadPage = lazy(() => import('@/pages/upload-page'))
const MyUploadsPage = lazy(() => import('@/pages/my-uploads-page'))
const AdminPage = lazy(() => import('@/pages/admin-page'))
const NotFoundPage = lazy(() => import('@/pages/not-found-page'))

const galleryLocation: Location = { pathname: '/', search: '', hash: '', state: null, key: 'gallery' }

/** Old `/search?q=` links. */
function SearchRedirect() {
  const [params] = useSearchParams()
  const q = params.get('q')
  return <Navigate to={q ? `/?q=${encodeURIComponent(q)}` : '/'} replace />
}

function Fallback() {
  return (
    <div className="flex justify-center py-24">
      <Spinner className="text-primary" />
    </div>
  )
}

/**
 * The image viewer is a modal route. Opened from a grid, the grid stays
 * rendered underneath (its location travels in `state.backgroundLocation`);
 * opened from a shared link, the gallery is shown behind it instead.
 */
export default function App() {
  const location = useLocation()
  const state = location.state as { backgroundLocation?: Location } | null
  const isViewer = /^\/imgb\/[^/]+\/?$/.test(location.pathname)
  const background = state?.backgroundLocation ?? (isViewer ? galleryLocation : undefined)

  return (
    <AppShell>
      <Suspense fallback={<Fallback />}>
        <Routes location={background ?? location}>
          <Route path="/" element={<GalleryPage />} />
          <Route path="/search" element={<SearchRedirect />} />
          <Route path="/videos" element={<Navigate to="/" replace />} />
          <Route path="/upload" element={<UploadPage />} />
          <Route path="/me" element={<MyUploadsPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>

      {isViewer && (
        <Suspense fallback={null}>
          <Routes>
            <Route path="/imgb/:id" element={<ImageViewer />} />
          </Routes>
        </Suspense>
      )}
    </AppShell>
  )
}
